import { Box, Button, Stack, Typography } from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LinkIcon from "@mui/icons-material/Link";
import { useTenantTranslation } from "hooks/formatters";
import { MassAdjustmentChart, MassChangeValues } from "../MassAdjustmentChart";
import { TimeAxisLayout } from "../timeAxisLayout";
import { Dayjs } from "dayjs";

interface MassAdjustmentSectionProps {
  eventId: string;
  eventStart: Dayjs;
  eventEnd: Dayjs;
  massEventStart: Dayjs | null;
  massEventEnd: Dayjs | null;
  eventMass: string | null;
  massEventMass: string | null;
  area: string | null | undefined;
  storedInitialMass: number | null;
  storedFinalMass: number | null;
  pairedEventStart?: Dayjs | null;
  pairedEventEnd?: Dayjs | null;
  playheadTime?: Dayjs;
  onMassChange: (values: MassChangeValues) => void;
  onAutoSync?: (values: MassChangeValues) => void;
  onTimeAxisChange?: (timeAxis: TimeAxisLayout | null) => void;
  canDetachMassEvent: boolean;
  isMassDetached: boolean;
  onToggleMassDetached: () => void;
}

export const MassAdjustmentSection = ({
  eventId,
  eventStart,
  eventEnd,
  massEventStart,
  massEventEnd,
  eventMass,
  massEventMass,
  area,
  storedInitialMass,
  storedFinalMass,
  pairedEventStart,
  pairedEventEnd,
  playheadTime,
  onMassChange,
  onAutoSync,
  onTimeAxisChange,
  canDetachMassEvent,
  isMassDetached,
  onToggleMassDetached,
}: MassAdjustmentSectionProps) => {
  const { t } = useTenantTranslation();

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2">{t("adjustMass")}</Typography>
        {canDetachMassEvent ? (
          <Button
            size="small"
            variant="outlined"
            color={isMassDetached ? "primary" : "warning"}
            startIcon={isMassDetached ? <LinkIcon /> : <LinkOffIcon />}
            onClick={onToggleMassDetached}
          >
            {isMassDetached ? t("reattachMassLabel") : t("detachMassLabel")}
          </Button>
        ) : null}
      </Stack>
      <MassAdjustmentChart
        eventId={eventId}
        eventStart={eventStart}
        eventEnd={eventEnd}
        massEventStart={massEventStart}
        massEventEnd={massEventEnd}
        eventMass={eventMass}
        massEventMass={massEventMass}
        area={area ?? null}
        storedInitialMass={storedInitialMass}
        storedFinalMass={storedFinalMass}
        pairedEventStart={pairedEventStart}
        pairedEventEnd={pairedEventEnd}
        playheadTime={playheadTime}
        onMassChange={onMassChange}
        onAutoSync={onAutoSync}
        onTimeAxisChange={onTimeAxisChange}
      />
    </Box>
  );
};
